import { Modal } from 'bootstrap';

import { Association, ServiceManager } from '@prisma/client';

import { routes } from '@/shared/routes';
import { ButtonAction } from '@/shared/types/button';
import { EditableRequestBody } from '@/shared/types/editable';
import { ClientType } from '@/shared/types/enum';
import { ModelName } from '@/shared/types/model';
import { pick } from '@/shared/utils/object';
import { StatusOptions } from '@/shared/utils/status';
import { MFA_CODE_REGEX, isValidCompanyNumber } from '@/shared/utils/validation';

import { getFormData, setEditableValue } from '@/front/editable/state';
import { DELETE, POST } from '@/front/fetch';
import { getLookupPicker } from '@/front/lookupPicker';
import { showModal } from '@/front/modal';
import { expandOffCanvas, hideOffcanvas } from '@/front/offcanvas';

document.addEventListener('DOMContentLoaded', () => initButtons());

const initButtons = () => {
  [...document.querySelectorAll('a[data-action], button[data-action]')]
    .map((button) => button as HTMLAnchorElement | HTMLButtonElement)
    .forEach(function (button) {
      button.addEventListener('click', async (event) => {
        event.preventDefault();

        const enabled = (button.dataset.enabled ?? 'true') === 'true';

        const confirmMessage = button.dataset.confirmMessage;

        if (enabled && (!confirmMessage || confirm(confirmMessage))) {
          // Prevent double-click
          button.dataset.enabled = 'false';

          const action = button.dataset.action as ButtonAction;

          if (action === 'submit') {
            submit(button);
          } else if (action === 'post') {
            await postForm(button);
          } else if (action === 'save') {
            save(button);
          } else if (action === 'delete') {
            del();
          } else if (action === 'expand') {
            expand();
          } else if (action === 'close') {
            close();
          } else if (action === 'resendInvite') {
            await resendInvite(button);
          } else if (action === 'resetPassword') {
            await resetPassword(button);
          } else if (action === 'enableMfa') {
            await enableMfa(button);
          } else if (action === 'disableMfa') {
            await disableMfa(button);
          } else if (action === 'importCompany') {
            importCompany(button);
          } else if (action === 'toggleSetDate') {
            await toggleSetDate(button);
          } else if (action === 'archive') {
            await archive(button, true);
          } else if (action === 'unarchive') {
            await archive(button, false);
          } else if (action === 'createSmartTasks') {
            await createSmartTasks(button);
          } else if (action === 'createCompanyServiceManager') {
            await createServiceManager(ClientType.Company, button);
          } else if (action === 'createIndividualServiceManager') {
            await createServiceManager(ClientType.Individual, button);
          } else if (action === 'deleteServiceManager') {
            await deleteServiceManager(button);
          } else if (action === 'createCompanyAssociation') {
            await createCompanyAssociation(button);
          } else if (action === 'createIndividualAssociation') {
            await createIndividualAssociation(button);
          } else if (action === 'deleteAssociation') {
            await deleteAssociation(button);
          } else if (action === 'undoCompanyImport') {
            await undoCompanyImport(button);
          } else if (action === 'undoIndividualImport') {
            await undoIndividualImport(button);
          } else if (action === 'previewTemplate') {
            await previewTemplate(button);
          }

          toggleIcon(button);

          // Prevent double-click
          button.dataset.enabled = 'true';
        }
      });
    });
};

const modelId = () => (document.getElementById('modelId') as HTMLInputElement | null)?.value;

const submit = async (button: HTMLAnchorElement | HTMLButtonElement) => {
  const form = button.closest('form') as HTMLFormElement | null;

  if (form && form.dataset.submitted !== 'true') {
    form.dataset.submitted = 'true';
    form.submit();
  }
};

const postForm = async (button: HTMLAnchorElement | HTMLButtonElement) => {
  const form = button.closest('form');

  if (form) {
    const data = Object.fromEntries(new FormData(form));

    const { res, status } = await post(form.action, data);

    delete form.dataset.dirty;

    if (res.ok) {
      if (status?.redirectUrl) {
        window.location.href = status?.redirectUrl;
      } else {
        // HACK: Assumes we're in offcanvas - might not be?
        // HACK: Assume there are grids = might not be?
        hideOffcanvas({ refreshGrids: true });
      }
    }
  }
};

const post = async (action: string, data?: any) => {
  const res = await POST(action, data);

  let status: StatusOptions | undefined;

  try {
    status = await res.json();
  } catch (error) {
    // No options - ignore
    console.info(error);
  }

  if (status?.message) {
    alert(status.message);
  }

  return { res, status };
};

const save = (button: HTMLAnchorElement | HTMLButtonElement) => {
  const form = button.closest('form');

  if (form) {
    form.dispatchEvent(new Event('save'));
  }
};

const del = async () => {
  await DELETE(window.location.href);
  close();
};

const expand = () => {
  expandOffCanvas();
};

const close = () => {
  hideOffcanvas();
};

const resendInvite = async (button: HTMLAnchorElement | HTMLButtonElement) => {
  const memberId = button.dataset.value;

  if (memberId) {
    await post(routes.members.resendInvite({ id: memberId }));
  }
};

const resetPassword = async (button: HTMLAnchorElement | HTMLButtonElement) => {
  const memberId = button.dataset.value;

  if (memberId) {
    await post(routes.members.resetPassword({ id: memberId }));
  }
};

const enableMfa = async (button: HTMLAnchorElement | HTMLButtonElement) => {
  const memberId = button.dataset.value;

  if (memberId) {
    const modalDiv = document.getElementById('mfa-modal') as HTMLDivElement;
    const qrImg = document.getElementById('mfa-qr') as HTMLImageElement;
    const codeInput = document.getElementById('mfa-code') as HTMLInputElement;
    const confirmButton = document.getElementById('mfa-confirm') as HTMLButtonElement;

    const res = await POST(routes.members.enableMfa({ id: memberId }));

    const { qrUrl } = await res.json();

    qrImg.src = qrUrl;

    const modal = new Modal(modalDiv);

    confirmButton.addEventListener('click', async () => {
      const code = codeInput.value;

      if (!MFA_CODE_REGEX.test(code)) {
        alert('Invalid code');
        return;
      }

      const res = await POST(routes.members.confirmMfa({ id: memberId }), { code });

      const { success } = await res.json();

      if (success) {
        modal.hide();
        window.location.reload();
      } else {
        codeInput.value = '';
        alert('Invalid code, please try again');
      }
    });

    modal.show();
  }
};

const disableMfa = async (button: HTMLAnchorElement | HTMLButtonElement) => {
  const memberId = button.dataset.value;

  if (memberId) {
    if (confirm('Are you sure you want to disable two-factor authentication?')) {
      await POST(routes.members.disableMfa({ id: memberId }));

      window.location.reload();
    }
  }
};

const importCompany = (button: HTMLAnchorElement | HTMLButtonElement) => {
  const form = button.closest('form');

  const input = form?.querySelector('[name="registrationNumber"]') as HTMLInputElement | null;

  const registrationNumber = input?.value;

  if (registrationNumber && isValidCompanyNumber(registrationNumber)) {
    const url = new URL(window.location.href);
    url.searchParams.set('registrationNumber', registrationNumber);
    window.location.replace(url.toString());
  }
};

const toggleSetDate = async (button: HTMLAnchorElement | HTMLButtonElement) => {
  const { editableId, value, toggled } = button.dataset;

  if (editableId) {
    const date = value ? new Date(Date.parse(value)) : new Date();

    await setEditableValue(editableId, toggled !== 'true' ? date : null);

    button.dataset.toggled = toggled !== 'true' ? 'true' : 'false';
  }
};

const toggleIcon = (button: HTMLAnchorElement | HTMLButtonElement) => {
  const toggleIcon = button.dataset.toggleIcon;
  const icon = button.querySelector('i');

  if (icon && toggleIcon) {
    const currentIcon = icon.className;
    icon.className = toggleIcon;
    button.dataset.toggleIcon = currentIcon;
  }
};

const archive = async (button: HTMLAnchorElement | HTMLButtonElement, archive: boolean) => {
  const modelName = button.dataset.model as ModelName | undefined;
  const id = button.dataset.value;

  if (confirm(`Are you sure you want to ${archive ? 'archive' : 'unarchive'} this record?`)) {
    if (modelName && id) {
      const body: EditableRequestBody = {
        params: {
          name: 'archivedTime',
          primaryKey: id,
          value: archive ? true : false,
        },
      };

      await POST(routes.data.editable({ model: modelName }), body);

      location.reload();
    }
  }
};

const createSmartTasks = async (button: HTMLAnchorElement | HTMLButtonElement) => {
  const accountId = button.dataset.value;

  if (accountId) {
    const res = await POST(routes.admin.accounts.createSmartTasks({ id: accountId }));

    const message = await res.text();

    if (message) {
      alert(message);
    }
  }
};

const createServiceManager = async (type: ClientType, button: HTMLAnchorElement | HTMLButtonElement) => {
  const servicePicker = getLookupPicker('serviceManagerServicePicker');
  const userPicker = getLookupPicker('serviceManagerUserPicker');

  if (servicePicker && userPicker) {
    const companyId = type === ClientType.Company ? modelId() : undefined;
    const individualId = type === ClientType.Individual ? modelId() : undefined;
    const serviceId = servicePicker?.value as string | undefined;
    const userId = userPicker?.value as string | undefined;

    if ((companyId || individualId) && serviceId && userId) {
      const serviceManager: Partial<ServiceManager> = {
        companyId,
        individualId,
        serviceId,
        userId,
      };

      const res = await POST(routes.serviceManagers.create(), serviceManager);

      if (res.ok) {
        button.closest('.table-like-row')?.insertAdjacentHTML('beforebegin', await res.text());
        initButtons();
        servicePicker.value = '';
        userPicker.value = '';
      }
    }
  }
};

const deleteServiceManager = async (button: HTMLAnchorElement | HTMLButtonElement) => {
  const id = button.dataset.value;

  if (id) {
    const res = await DELETE(routes.serviceManagers.edit({ id }));

    if (res.ok) {
      button.closest('.table-like-row')?.remove();
    }

    getLookupPicker('serviceManagerServicePicker')?.refresh();
    getLookupPicker('serviceManagerUserPicker')?.refresh();
  }
};

const createIndividualAssociation = async (button: HTMLAnchorElement | HTMLButtonElement) => {
  const picker = getLookupPicker('individualPicker');

  if (picker) {
    const companyId = modelId();
    const individualId = picker?.value as string | undefined;

    if (companyId && individualId) {
      const association: Partial<Association> = {
        companyId,
        individualId,
      };

      const res = await POST(`${routes.associations.create()}?view=Company`, association);

      if (res.ok) {
        button.closest('tr')?.insertAdjacentHTML('beforebegin', await res.text());
        initButtons();
        picker.value = '';
      }
    }
  }
};

const createCompanyAssociation = async (button: HTMLAnchorElement | HTMLButtonElement) => {
  const picker = getLookupPicker('companyPicker');

  if (picker) {
    const individualId = modelId();
    const companyId = picker.value as string | undefined;

    if (individualId && companyId) {
      const association: Partial<Association> = {
        individualId,
        companyId,
      };

      const res = await POST(`${routes.associations.create()}?view=Individual`, association);

      if (res.ok) {
        button.closest('tr')?.insertAdjacentHTML('beforebegin', await res.text());
        initButtons();
        picker.value = '';
      }
    }
  }
};

const deleteAssociation = async (button: HTMLAnchorElement | HTMLButtonElement) => {
  const id = button.dataset.value;

  if (id) {
    const res = await DELETE(routes.associations.edit({ id }));

    if (res.ok) {
      button.closest('tr')?.remove();

      getLookupPicker('companyPicker')?.refresh();
      getLookupPicker('individualPicker')?.refresh();
    }
  }
};

const undoCompanyImport = async (button: HTMLAnchorElement | HTMLButtonElement) => {
  const id = button.dataset.value;

  if (id) {
    if (confirm('Are you sure you want to undo your last import? This action can NOT be reversed.')) {
      await DELETE(routes.companies.import.delete({ id }));
      close();
    }
  }
};

const undoIndividualImport = async (button: HTMLAnchorElement | HTMLButtonElement) => {
  const id = button.dataset.value;

  if (id) {
    if (confirm('Are you sure you want to undo your last import? This action can NOT be reversed.')) {
      await DELETE(routes.individuals.import.delete({ id }));
      close();
    }
  }
};

const previewTemplate = async (button: HTMLAnchorElement | HTMLButtonElement) => {
  const form = button.closest('form') as HTMLFormElement | null;

  if (form) {
    const data = pick(getFormData(form), 'subject', 'content');

    const res = await POST(routes.templates.preview(), data);

    const { subject, content, signature } = await res.json();

    showModal({
      title: subject,
      body: content ? `${content}${signature ?? ''}` : signature,
      fullScreen: true,
    });
  }
};
